import { useEffect, useRef, useState } from "react";
import {
  GroupNamesDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { GroupNamesService } from "../../services/group_names.service";
import SelectCategory from "../../components/admin-pages/products/select-category/SelectCategory";
import { FaPlus } from "react-icons/fa";

const GroupNames = () => {
  const [groupNames, setGroupNames] = useState<GroupNamesDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<GroupNamesDataType | null>(
    null
  );
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const initialGroupNamesValues = useRef({
    name: "",
    category_id: 0,
    translations: [] as { lang_code: string; name: string }[],
  });
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  useEffect(() => {
    setLoading(true);
    GroupNamesService.getAllGroupNames()
      .then((response) => {
        if (Array.isArray(response?.data)) {
          const dataWithKeys = response?.data?.map(
            (item: GroupNamesDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setGroupNames(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await GroupNamesService.getAllGroupNames();
    setGroupNames(
      updatedData.data.map((item: GroupNamesDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        name: "",
        category_id: "",
        translations: [] as { lang_code: string; name: string }[],
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Qrup adı tələb olunur"),
        category_id: Yup.string().required("Kategoriya adı tələb olunur"),
      }),
      onSubmit: async (values) => {
        const azTranslation = {
          lang_code: "AZ",
          name: values.name,
        };
        const updatedTranslations = [
          azTranslation,
          ...values.translations.filter((t) => t.lang_code !== "AZ"),
        ];
        const finalValues = {
          ...values,
          translations: updatedTranslations,
        };
        if (isEditing && editingRecord) {
          const isEqual =
            JSON.stringify(values.name) ===
              JSON.stringify(initialGroupNamesValues.current.name) &&
            JSON.stringify(values.category_id) ===
              JSON.stringify(initialGroupNamesValues.current.category_id) &&
            JSON.stringify(values.translations) ===
              JSON.stringify(initialGroupNamesValues.current.translations);
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            const response = await GroupNamesService.updateGroupName({
              ...finalValues,
              id: editingRecord.id,
            });
            if (response.status === 200) {
              message.success("Qrup adı uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Qrup adı yeniləmək alınmadı."
              );
            }
          }
        } else {
          const response = await GroupNamesService.addGroupName(finalValues);
          if (response.status === 200) {
            message.success("Qrup adı uğurla əlavə edildi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Qrup adı əlavə etmək alınmadı."
            );
          }
        }
        setLoading(false);
      },
    });
  const handleTranslationChange = (value: string) => {
    if (langCode === "AZ") {
      setFieldValue("name", value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          name: value,
        };
      } else {
        updatedTranslations.push({ lang_code: langCode, name: value });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleDeleteClick = async (record: GroupNamesDataType) => {
    try {
      await GroupNamesService.deleteGroupName(record.id.toString());
      setGroupNames((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Qrup adı uğurla silindi!");
    } catch (error) {
      message.error("Qrup adını silmək alınmadı.");
    }
  };
  const columns: TableProps<GroupNamesDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Qrup adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Kateqoriya adı",
      dataIndex: "category",
      render: (_, record) => {
        return record.category?.name || "-";
      },
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu qrup adını siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
    setSelectedCategoryId(null);
  };
  const handleEditClick = (record: GroupNamesDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("category_id", record.category_id);
    setFieldValue("translations", record.translations || []);
    setLangCode("AZ");
    initialGroupNamesValues.current = {
      name: record.name,
      category_id: record.category_id,
      translations: record.translations || [],
    };
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Qrup adları</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Məhsulun xüsusiyyətlərinin qrupları</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<GroupNamesDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={groupNames}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          isEditing ? "Qrup adını redaktə edin" : "Yeni qrup adı əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedCategoryId(null);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Qrup adı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Qrup adı"
                value={
                  langCode === "AZ"
                    ? values.name
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.name || ""
                }
                onChange={(e) => handleTranslationChange(e.target.value)}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div>
            <div className="form_input">
              <div className="input_wrapper">
                <SelectCategory
                  setFieldValue={setFieldValue}
                  categroryName={editingRecord?.category?.name}
                  selectedCategoryId={selectedCategoryId}
                  setSelectedCategoryId={setSelectedCategoryId}
                />
              </div>
              <ErrorMessage
                error={errors.category_id}
                touched={touched.category_id}
              />
            </div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? " Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GroupNames;
